/**
 * Widget V2 — Location detail (discovery)
 *
 * Address (+ map link), opening hours, contact, sports offered, and CTAs that
 * deep-link into booking scoped to this location (`?locationId=…`).
 */

import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState } from "/imports/ui/components/shared";
import { PageHeading, SECTION_LABEL_SX, DISCOVERY_CARD_SX, useBackTo } from "/imports/ui/components/discovery";
import { LocationBanner } from "./LocationsView";

const DAYS = [
  ["monday", "Mon"],
  ["tuesday", "Tue"],
  ["wednesday", "Wed"],
  ["thursday", "Thu"],
  ["friday", "Fri"],
  ["saturday", "Sat"],
  ["sunday", "Sun"]
];

const pad = (n) => String(n).padStart(2, "0");
const minutesToHHMM = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;

function dayHours(entry) {
  if (!entry || !entry.isOn || !(entry.shifts || []).length) return "Closed";
  return entry.shifts.map((s) => `${minutesToHHMM(parseInt(s.from, 10))} – ${minutesToHHMM(parseInt(s.to, 10))}`).join(", ");
}

// Booking types we can deep-link into from a location, by service category.
const LOCATION_CTAS = [
  { categoryType: "category-service-court", route: "courts", label: "Book a court", icon: SportsTennisIcon },
  { categoryType: "category-service-private", route: "lessons", label: "Book a lesson", icon: SupervisorAccountOutlinedIcon },
  { categoryType: "category-service-class", route: "classes", label: "Join a class", icon: Diversity3OutlinedIcon }
];

function LocationDetailView() {
  const navigate = useNavigate();
  const { locationId } = useParams();
  const { slug, locations, sports, services } = useWidget();

  const goBack = useBackTo(`/${slug}/locations`);

  const loc = useMemo(() => (locations || []).find((l) => l._id === locationId), [locations, locationId]);

  const locSports = useMemo(() => {
    const ids = new Set(loc?.sportIds || []);
    return (sports || []).filter((s) => ids.has(s._id));
  }, [loc, sports]);

  const availableCtas = useMemo(() => {
    const types = new Set((services || []).map((s) => s.categoryType));
    return LOCATION_CTAS.filter((c) => types.has(c.categoryType));
  }, [services]);

  if (!loc) {
    return (
      <Box>
        <PageHeading title="Location" onBack={goBack} />
        <EmptyState title="Location not found" actionLabel="Back to locations" onAction={() => navigate(`/${slug}/locations`)} />
      </Box>
    );
  }

  const hasHours = loc.openingHours && DAYS.some(([key]) => loc.openingHours[key]);
  const mapHref =
    loc.lat && loc.lng
      ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`
      : loc.location
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.location)}`
        : null;

  return (
    <Box>
      <PageHeading title={loc.name} onBack={goBack} />

      <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden", mb: 3 }}>
        <LocationBanner height={160} src={loc.imgUrls?.[0]} />
        <Box sx={{ p: 3 }}>
          {loc.location && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.5 }}>
              <LocationOnIcon sx={{ color: "primary.main", mt: 0.25 }} fontSize="small" />
              <Box>
                <Typography variant="body1">{loc.location}</Typography>
                {mapHref && (
                  <Link
                    href={mapHref}
                    target="_blank"
                    rel="noopener"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.25, fontWeight: 600 }}
                  >
                    Open in Maps <OpenInNewIcon sx={{ fontSize: "0.875rem" }} />
                  </Link>
                )}
              </Box>
            </Box>
          )}

          {(loc.phoneNumber || loc.email) && (
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1 }}>
              {loc.phoneNumber && (
                <Link href={`tel:${loc.phoneNumber}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "text.primary" }}>
                  <PhoneIcon fontSize="small" sx={{ color: "primary.main" }} /> {loc.phoneNumber}
                </Link>
              )}
              {loc.email && (
                <Link href={`mailto:${loc.email}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: "text.primary" }}>
                  <EmailIcon fontSize="small" sx={{ color: "primary.main" }} /> {loc.email}
                </Link>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {hasHours && (
          <Grid item xs={12} md={6}>
            <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>Opening hours</Typography>
            <Box sx={{ ...DISCOVERY_CARD_SX, p: 2 }}>
              {DAYS.map(([key, label]) => {
                const text = dayHours(loc.openingHours[key]);
                const closed = text === "Closed";
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.6,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: 0 }
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {label}
                    </Typography>
                    <Typography variant="body2" color={closed ? "text.disabled" : "text.secondary"}>
                      {text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
        )}

        <Grid item xs={12} md={hasHours ? 6 : 12}>
          {locSports.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>Sports here</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                {locSports.map((s) => (
                  <Chip
                    key={s._id}
                    label={s.name}
                    sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: "primary.main", fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {availableCtas.length > 0 && (
            <Box>
              <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>Book here</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {availableCtas.map((cta) => (
                  <Button
                    key={cta.route}
                    variant="outlined"
                    size="large"
                    startIcon={<cta.icon />}
                    onClick={() => navigate(`/${slug}/${cta.route}?locationId=${loc._id}`)}
                    sx={{ justifyContent: "flex-start", py: 1.25, textTransform: "none", fontWeight: 600 }}
                  >
                    {cta.label}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default LocationDetailView;
